var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '~/providers/user';
import { useWebsocket } from '~/providers/websocket';
import { SearchType } from '../enums';
import createDocumentComparison from './actions/createDocumentComparison';
import getClauseParentMetadata from './actions/get_clause_parent_metadata';
import oldSearchDocuments from './actions/oldSearchDocuments';
import searchClauses from './actions/search_clauses';
import searchDocuments from './actions/searchDocuments';
const ContentLibraryContext = createContext({});
const ContentLibraryProvider = (props) => {
    const { search: locationSearch } = useLocation();
    const navigate = useNavigate();
    const searchRef = useRef();
    const { useNewView } = useUser();
    const ws = useWebsocket();
    const [searchedClauses, setSearchedClauses] = useState(null);
    const [oldQueryInfo, setOldQueryInfo] = useState(null);
    const [queryInfo, setQueryInfo] = useState(null);
    const [activeClause, setActiveClause] = useState(null);
    const [clauseParentMetadata, setClauseParentMetadata] = useState([]);
    const [oldSearchedDocuments, setOldSearchedDocuments] = useState(null);
    const [activeDocument, setActiveDocument] = useState(null);
    const [searchedDocuments, setSearchedDocuments] = useState(null);
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [isSearchInProgress, setIsSearchInProgress] = useState(false);
    const [isCreatingComparison, setIsCreatingComparison] = useState(false);
    useEffect(() => {
        searchRef.current = locationSearch;
        performSearch();
    }, [locationSearch]);
    const performSearch = () => __awaiter(void 0, void 0, void 0, function* () {
        const searchParams = new URLSearchParams(locationSearch);
        const filtersString = searchParams.get('filters');
        if (useNewView) {
            setIsSearchInProgress(true);
            setSearchedClauses(null);
            setActiveClause(null);
            setOldSearchedDocuments(null);
            setSearchedDocuments(null);
            setActiveDocument(null);
            const searchFilters = JSON.parse(filtersString !== null && filtersString !== void 0 ? filtersString : '{}');
            yield searchDocuments([], setSearchedDocuments, setQueryInfo, searchFilters.searchText);
            setIsSearchInProgress(false);
            return;
        }
        if (filtersString) {
            setIsSearchInProgress(true);
            setSearchedClauses(null);
            setActiveClause(null);
            setOldSearchedDocuments(null);
            setSearchedDocuments(null);
            setActiveDocument(null);
            const searchFilters = JSON.parse(filtersString);
            if (searchFilters.searchType === SearchType.Documents) {
                const { searchedDocs, query } = yield oldSearchDocuments(ws, searchFilters, 1);
                if (searchRef.current !== locationSearch)
                    return;
                setOldQueryInfo(query);
                setOldSearchedDocuments(searchedDocs);
            }
            if (searchFilters.searchType === SearchType.Clauses) {
                const { clauses, query } = yield searchClauses(ws, searchFilters, 1);
                if (searchRef.current !== locationSearch)
                    return;
                setOldQueryInfo(query);
                setSearchedClauses(clauses);
            }
            setIsSearchInProgress(false);
        }
    });
    const getMoreClauses = () => __awaiter(void 0, void 0, void 0, function* () {
        const searchParams = new URLSearchParams(locationSearch);
        const filtersString = searchParams.get('filters');
        if (filtersString) {
            const searchFilters = JSON.parse(filtersString);
            if (searchFilters.searchType === SearchType.Clauses) {
                const { clauses, query } = yield searchClauses(ws, searchFilters, (oldQueryInfo === null || oldQueryInfo === void 0 ? void 0 : oldQueryInfo.pageNumber) ? oldQueryInfo.pageNumber + 1 : 1);
                setOldQueryInfo(query);
                setSearchedClauses([...(searchedClauses || []), ...clauses]);
            }
        }
    });
    const getMoreDocuments = () => __awaiter(void 0, void 0, void 0, function* () {
        const searchParams = new URLSearchParams(locationSearch);
        const filtersString = searchParams.get('filters');
        if (useNewView) {
            if (!searchedDocuments)
                return;
            const searchFilters = JSON.parse(filtersString !== null && filtersString !== void 0 ? filtersString : '{}');
            yield searchDocuments(searchedDocuments, setSearchedDocuments, setQueryInfo, searchFilters.searchText);
            setIsSearchInProgress(false);
            return;
        }
        if (filtersString) {
            const searchFilters = JSON.parse(filtersString);
            if (searchFilters.searchType === SearchType.Documents) {
                const { searchedDocs, query } = yield oldSearchDocuments(ws, searchFilters, (oldQueryInfo === null || oldQueryInfo === void 0 ? void 0 : oldQueryInfo.pageNumber) ? oldQueryInfo.pageNumber + 1 : 1);
                setOldQueryInfo(query);
                setOldSearchedDocuments([...(oldSearchedDocuments || []), ...searchedDocs]);
            }
        }
    });
    const selectClause = (clause) => __awaiter(void 0, void 0, void 0, function* () {
        setActiveClause(clause);
        setClauseParentMetadata(yield getClauseParentMetadata(clauseParentMetadata, clause));
    });
    const toggleSelectedDocument = (doc) => {
        if (selectedDocuments.find(sd => sd.id === doc.id)) {
            setSelectedDocuments(selectedDocuments.filter(sd => sd.id !== doc.id));
        }
        else {
            setSelectedDocuments([...selectedDocuments, doc]);
        }
    };
    const selectManyDocuments = (docs, deselect = false) => {
        if (deselect) {
            setSelectedDocuments(selectedDocuments.filter(sd => !docs.find(doc => doc.id === sd.id)));
        }
        else {
            setSelectedDocuments([...selectedDocuments, ...docs]);
        }
    };
    const clearSelectedDocuments = () => {
        setSelectedDocuments([]);
    };
    const createComparison = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsCreatingComparison(true);
        const response = yield createDocumentComparison(selectedDocuments);
        if (response.compare_id)
            navigate(`/compare/${response.compare_id}/grid`);
    });
    const value = {
        searchedClauses,
        oldQueryInfo,
        queryInfo,
        activeClause,
        selectClause,
        clauseParentMetadata,
        getMoreClauses,
        getMoreDocuments,
        performSearch,
        oldSearchedDocuments,
        searchedDocuments,
        activeDocument,
        setActiveDocument,
        selectedDocuments,
        toggleSelectedDocument,
        selectManyDocuments,
        clearSelectedDocuments,
        isSearchInProgress,
        createComparison,
        isCreatingComparison,
    };
    return React.createElement(ContentLibraryContext.Provider, Object.assign({ value: value }, props));
};
const useContentLibrary = () => {
    const context = useContext(ContentLibraryContext);
    if (!context) {
        throw new Error('ContentLibraryContext must be used inside of a ContentLibraryProvider');
    }
    return context;
};
export { ContentLibraryProvider, useContentLibrary };
